import { Avatar, Box, Checkbox, FormControlLabel, Stack, Typography, useTheme } from '@mui/material'
import { Security } from '../../../../proto/generated/security'
import { memo, useCallback, useContext, useMemo } from 'react'
import './seriesMetadataDisplay.css'
import { getSecurityKey } from '../../../../util/security'
import { ChartViewKey, LOGOS_BASE_URL } from '../../../../assets/constants'
import LiveText from '../../../common/LiveText'
import {
    buildMultiStrikeMetadataSubscribe,
    getMultiStrikePrimaryValueSelector,
} from './multiStrikeMetadataLiveDisplay'
import MultiStrikeMetadataWidgetControls from './MultiStrikeMetadataWidgetControls'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectMultiStrikeChartConfigChartType,
    selectMultiStrikeChartConfigCumOi,
    selectMultiStrikeChartConfigExpiry,
    selectMultiStrikeChartConfigShowChange,
    selectMultiStrikeStatusLineSettings,
} from '../../../../selectors/chart'
import { getColorForSymbol } from '../../../../util/general'
import SeriesTitleButton from '../../../chartV2/seriesMetaDisplay/SeriesTitleButton'
import { setSecuritySearchOpen } from '../../../../reducer/home.reducer'
import OptionsExpiryMenuBody from '../../../../pages/home/optionsChartHeader/optionsChartExpiryMenu/optionsExpiryMenuBody'
import { DropDownExpiries } from '../../../../reducer/options.reducer'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { SearchParamsContext } from '../../../providers/searchParamsProvider'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { AppContext } from '../../../../App'
import {
    setMultiStrikeCumOiToggle,
} from '../../../../reducer/multiStrikeChart.reducer'
import { selectMultiStrikeChartType, selectMultiStrikeCumOi, selectMultiStrikeShowChange } from '../../../../selectors/multiStrikeChart'
import { DataUpdateRequestType } from '../../../../util/chart'
import { resolveCumOiToggleState, showsCumOiToggle, usesCumulativeFlag } from '../../multistrikeChartTypeHelpers'
import { getMultiStrikeMetadataRowSx, MULTI_STRIKE_METADATA_ROW_CLASS } from './multiStrikeMetadataLayout'

export type MultiStrikeMetadataHeaderProps = {
    security: Security | undefined
    viewKey: string
    primarySecKey: string
    displayCumulativeToggle?: boolean
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
}

const MultiStrikeMetadataHeader = ({
    security,
    viewKey,
    primarySecKey,
    displayCumulativeToggle = true,
    onSecuritySelected,
}: MultiStrikeMetadataHeaderProps) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const searchParamsCtx = useContext(SearchParamsContext)
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const showChange = useSelector(isWidget ? selectMultiStrikeChartConfigShowChange(viewKey) : selectMultiStrikeShowChange)
    const showCumOiToggle = displayCumulativeToggle && showsCumOiToggle(chartType)
    const statusLineSettings = useSelector(selectMultiStrikeStatusLineSettings(viewKey))
    const configExpiry = useSelector(selectMultiStrikeChartConfigExpiry(viewKey))

    const subscribe = useMemo(
        () => buildMultiStrikeMetadataSubscribe(viewKey, [primarySecKey]),
        [viewKey, primarySecKey]
    )
    const selectText = useMemo(
        () => getMultiStrikePrimaryValueSelector(viewKey, primarySecKey),
        [viewKey, primarySecKey]
    )

    const showLogo = statusLineSettings.logo ?? true
    const showSymbolName = statusLineSettings.showSymbolName ?? true
    const showOHLC = statusLineSettings.showOHLC ?? true

    const symbolText = security?.symbol ?? security?.code ?? ''

    const logoNode = useMemo(() => {
        if (!showLogo || !security) return null
        if (security.logoUrl) {
            return (
                <Avatar
                    alt={symbolText}
                    src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`}
                    sx={{ width: 18, height: 18, flexShrink: 0 }}
                />
            )
        }
        return (
            <Avatar
                alt={symbolText}
                sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    fontSize: 10,
                    color: 'white',
                    bgcolor: getColorForSymbol(security.symbol || security.code || '', theme.palette.background.default),
                }}
            >
                {(security.symbol || security.code || '')
                    .split(' ')
                    .map((word) => word[0]?.toUpperCase())
                    .slice(0, 2)
                    .join('')}
            </Avatar>
        )
    }, [security, showLogo, symbolText, theme.palette.background.default])

    const onTitleClick = useCallback(() => {
        if (!security) return
        const secKey = getSecurityKey(security.exchange, security.code)
        dispatch(
            setSecuritySearchOpen({
                open: true,
                key: secKey,
                isExpression: false,
                onSecuritySelected,
            })
        )
    }, [dispatch, onSecuritySelected, security])

    const underlyingToken = useMemo(
        () => (security ? getSecurityKey(security.exchange, security.code) : ''),
        [security]
    )

    const expiryLabel = useMemo(() => {
        if (isWidget) return configExpiry ?? ''
        return decodeURIComponent(searchParamsCtx?.searchParams.get('expiry') || '')
    }, [configExpiry, isWidget, searchParamsCtx?.searchParams])

    const handleExpirySelect = useCallback(
        (dropdownExpiry: DropDownExpiries) => {
            const name = dropdownExpiry.expiry.name
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({ viewKey, config: { expiry: name } }))
            } else if (searchParamsCtx?.searchParams && searchParamsCtx.setSearchParams) {
                searchParamsCtx.searchParams.set('expiry', name)
                searchParamsCtx.setSearchParams(searchParamsCtx.searchParams)
            }
            AnalyticsManager.logEvent('Expiry Selected', { selected_value: name, source: 'multistrike' })
        },
        [dispatch, isWidget, searchParamsCtx, viewKey]
    )

    const handleCumOiChange = useCallback(
        (checked: boolean) => {
            const chart = appContext.getMultiStrikeChart(viewKey)
            const { chartType: nextChartType, cumOi: nextCumOi, showChange: nextShowChange } = resolveCumOiToggleState(
                checked,
                chartType,
                showChange
            )
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({
                    viewKey,
                    config: { chartType: nextChartType, cumOi: nextCumOi, showChange: nextShowChange },
                }))
            } else {
                dispatch(setMultiStrikeCumOiToggle({
                    chartType: nextChartType,
                    cumOi: nextCumOi,
                    showChange: nextShowChange,
                }))
            }
            if (chart) {
                chart.chartType = nextChartType
                chart.showChange = nextShowChange
                chart.cumOi = nextCumOi
                if (usesCumulativeFlag(nextChartType)) {
                    if (nextChartType === 'straddle') {
                        chart.straddleSeries.refresh()
                    } else if (nextChartType === 'oiDiff') {
                        chart.oiDiffSeries.refresh()
                    }
                    chart.onCrosshairMove({} as any)
                } else {
                    chart.onDataLoaded(DataUpdateRequestType.LIVE, true, false, false)
                    chart.dataManager.fetchData({})
                    chart.onCrosshairMove({} as any)
                }
            }
            AnalyticsManager.logEvent('Chart Type Changed', { type: nextChartType, source: 'multistrike' })
        },
        [appContext, chartType, dispatch, isWidget, showChange, viewKey]
    )

    return (
        <Typography
            color={'textSecondary'}
            component={'div'}
            className={`series-name flex items-center ${MULTI_STRIKE_METADATA_ROW_CLASS}`}
            sx={getMultiStrikeMetadataRowSx(theme, { fullWidth: true })}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: 0.25,
                    fontWeight: 500,
                    minWidth: 0,
                    maxWidth: '100%',
                    width: '100%',
                    ...(showCumOiToggle ? { pr: '92px' } : {}),
                }}
                className="font-feature-num"
            >
                {(showLogo || (showSymbolName && symbolText)) ? (
                    <SeriesTitleButton onClick={onTitleClick}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {showLogo ? logoNode : null}
                            {showSymbolName && symbolText ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {symbolText}
                                </Typography>
                            ) : null}
                        </Stack>
                    </SeriesTitleButton>
                ) : null}
                {expiryLabel ? (
                    <OptionsExpiryMenuBody
                        underlyingToken={underlyingToken}
                        expiryLabel={expiryLabel}
                        onSelect={handleExpirySelect}
                        hidePastExpiry
                        hideDerivedInMenu
                        excludeDerivedDropdown
                    />
                ) : null}
                {isWidget ? (
                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MultiStrikeMetadataWidgetControls viewKey={viewKey} />
                    </Box>
                ) : null}
                {showOHLC ? (
                    <Box sx={{ flexShrink: 0, minWidth: '72px' }}>
                        <LiveText subscribe={subscribe} selectText={selectText} style={{ fontSize: '13px' }} />
                    </Box>
                ) : null}
            </Box>
            {showCumOiToggle ? (
                <FormControlLabel
                    sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        m: 0,
                        flexShrink: 0,
                        '& .MuiFormControlLabel-label': { fontSize: '13px' },
                    }}
                    control={
                        <Checkbox
                            size="small"
                            checked={cumOi}
                            onChange={(_event, checked) => handleCumOiChange(checked)}
                            sx={{ p: 0.25 }}
                        />
                    }
                    label="Cum. OI"
                />
            ) : null}
        </Typography>
    )
}

export default memo(MultiStrikeMetadataHeader, (prev, next) => {
    return (
        prev.viewKey === next.viewKey &&
        prev.primarySecKey === next.primarySecKey &&
        getSecurityKey(prev.security?.exchange ?? 0, prev.security?.code ?? '') ===
            getSecurityKey(next.security?.exchange ?? 0, next.security?.code ?? '') &&
        prev.security?.symbol === next.security?.symbol &&
        prev.security?.code === next.security?.code &&
        prev.security?.logoUrl === next.security?.logoUrl
    )
})
